import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems, toInt } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import ShipmentCriteriaTab from './shipmentVehicle/ShipmentCriteriaTab';
import { usePageActions } from '../contexts/PermissionContext';
import { useFullRecordRefill } from './detail/useFullRecordRefill';

const API_BASE = '/api/shipmentvehicles';

const EMPTY_CRITERIA = {
  customerGroupIds: [], subGroupIds: [], dynamicGroupIds: [], salesRepIds: [],
  additionalFields: [], shipmentArea: { polygons: [] },
};

// CriteriaJson (DB) → kriter state'i; bozuk/boş JSON güvenli boş şemaya düşer.
function parseCriteria(json) {
  if (!json) return { ...EMPTY_CRITERIA };
  try {
    const o = typeof json === 'string' ? JSON.parse(json) : json;
    return {
      customerGroupIds: o.customerGroupIds || [],
      subGroupIds: o.subGroupIds || [],
      dynamicGroupIds: o.dynamicGroupIds || [],
      salesRepIds: o.salesRepIds || [],
      additionalFields: o.additionalFields || [],
      shipmentArea: { polygons: o.shipmentArea?.polygons || [] },
    };
  } catch {
    return { ...EMPTY_CRITERIA };
  }
}

const TABS = [
  { key: 'vehicle', label: 'Araç Bilgileri', icon: 'box' },
  { key: 'criteria', label: 'Kriter', icon: 'mapPin' },
];

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Bakımda', tone: 'wait' },
};
const STATUS_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  ...Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label })),
];

const EMPTY_FORM = { plaka: '', brand: '', model: '', volume: '', maxWeight: '', status: '', vendorId: '', warehouseId: '', distributorId: '' };
const EMPTY_FORM_NEW = { plaka: '', brand: '', model: '', volume: '', maxWeight: '', status: 1, vendorId: '', warehouseId: '', distributorId: '' };

const toNum = (v) => (v === '' || v == null ? null : Number(v));

const rowToForm = (row) => ({
  plaka: row.plaka || '', brand: row.brand || '', model: row.model || '',
  volume: row.volume ?? '', maxWeight: row.maxWeight ?? '', status: row.status ?? '',
  vendorId: row.vendorId || '', warehouseId: row.warehouseId || '', distributorId: row.distributorId || '',
});

export function ShipmentVehicleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Sevkiyat Aracı' : 'Sevkiyat Aracı Detayı', 'Sevkiyat araç tanımı.');

  const [form, setForm] = useState(isNew ? EMPTY_FORM_NEW : EMPTY_FORM);
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [activeTab, setActiveTab] = useState('vehicle');
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  // #770 Kullanıcı forma dokunduysa arka plandaki tam-kayıt tazelemesi onu ezmesin.
  const { markDirty, refillIfClean } = useFullRecordRefill();
  const set = (k, v) => { markDirty(); setForm((c) => ({ ...c, [k]: v })); };

  const getVendorLabel = useCallback((vid) => {
    const v = vendors.find((item) => item.id === vid);
    if (!v) return '-';
    return v.name ? `${v.textId || ''} - ${v.name}`.trim().replace(/^- /, '') : v.textId || '-';
  }, [vendors]);

  const getWarehouseLabel = useCallback((wid) => {
    const w = warehouses.find((item) => item.id === wid);
    if (!w) return '-';
    return w.name ? `${w.textId || ''} - ${w.name}`.trim().replace(/^- /, '') : w.textId || '-';
  }, [warehouses]);

  const getDistributorLabel = useCallback((did) => {
    const d = distributors.find((item) => item.id === did);
    if (!d) return '-';
    return d.name ? `${d.textId || ''} - ${d.name}`.trim().replace(/^- /, '') : d.textId || '-';
  }, [distributors]);

  // Lookup'lar (etiket + RelationPicker)
  useEffect(() => {
    Promise.all([
      fetch('/api/vendors/Select', { headers: authHeaders() }).then((res) => res.ok ? res.json() : []).catch(() => []),
      fetch('/api/warehouses/Select', { headers: authHeaders() }).then((res) => res.ok ? res.json() : []).catch(() => []),
      fetch('/api/distributors/Select', { headers: authHeaders() }).then((res) => res.ok ? res.json() : []).catch(() => []),
    ]).then(([vData, wData, dData]) => { setVendors(unwrapLookupItems(vData)); setWarehouses(unwrapLookupItems(wData)); setDistributors(unwrapLookupItems(dData)); });
  }, []);

  // Tek kayıt yükleme: önce location.state.row; yoksa OData $filter ile id'ye göre çek.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    if (location.state?.row) {
      setForm(rowToForm(location.state.row));
      setCriteria(parseCriteria(location.state.row.criteriaJson));
      // #770.1/2 Nav-state satırı liste OData $select'inden gelir → YALNIZ görünür kolonlar.
      // Dağıtıcı (distributorId) ve Kriterler sekmesi (criteriaJson) projeksiyonda olmadığı
      // için tanım tekrar açıldığında boş görünüyordu. Tam kaydı NON-BLOCKING çekip formu,
      // kriterleri ve audit'i ondan tazeliyoruz.
      fetch(`${API_BASE}?$top=1&$filter=${encodeURIComponent(`id eq ${id}`)}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const rec = data ? getODataRows(data)[0] : null;
          if (!rec) return;
          refillIfClean(() => { setForm(rowToForm(rec)); setCriteria(parseCriteria(rec.criteriaJson)); });
          setAudit({ createdBy: rec.createdBy ?? rec.CreatedBy ?? rec.createdUser ?? rec.CreatedUser, createdDate: rec.createdDate ?? rec.CreatedDate, updatedBy: rec.updatedBy ?? rec.UpdatedBy ?? rec.modifiedUser ?? rec.ModifiedUser, updatedDate: rec.updatedDate ?? rec.UpdatedDate ?? rec.modifiedDate ?? rec.ModifiedDate });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const url = `${API_BASE}?$top=1&$filter=${encodeURIComponent(`id eq ${id}`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Araç alınamadı (${res.status})`);
      const data = await res.json();
      const found = getODataRows(data)[0];
      if (!found) throw new Error('Araç bulunamadı');
      setForm(rowToForm(found));
      setCriteria(parseCriteria(found.criteriaJson));
      setAudit({ createdBy: found.createdBy ?? found.CreatedBy ?? found.createdUser ?? found.CreatedUser, createdDate: found.createdDate ?? found.CreatedDate, updatedBy: found.updatedBy ?? found.UpdatedBy ?? found.modifiedUser ?? found.ModifiedUser, updatedDate: found.updatedDate ?? found.UpdatedDate ?? found.modifiedDate ?? found.ModifiedDate });
    } catch (err) {
      showToast('error', err.message || 'Araç alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state, refillIfClean]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /shipment-vehicles/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — Kaydet'e kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setCriteria(location.state.cloneCriteria ? parseCriteria(location.state.cloneCriteria) : { ...EMPTY_CRITERIA });
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    plaka: form.plaka, brand: form.brand || null, model: form.model || null,
    volume: toNum(form.volume), maxWeight: toNum(form.maxWeight), status: toInt(form.status),
    vendorId: form.vendorId, warehouseId: form.warehouseId || null,
    distributorId: form.distributorId || null,
    criteriaJson: JSON.stringify(criteria),
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp yeni boş kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.plaka.trim()) { setActiveTab('vehicle'); showToast('error', 'Plaka boş olamaz'); return; }
    if (!form.vendorId) { setActiveTab('vehicle'); showToast('error', 'Distribütör seçimi zorunludur'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      if (!res.ok) throw new Error(isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi');
      if (isNew) {
        if (andNew) {
          showToast('success', 'Araç eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM_NEW);
          setCriteria({ ...EMPTY_CRITERIA });
          setActiveTab('vehicle');
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Araç başarıyla eklendi.');
        setTimeout(() => navigate('/shipment-vehicles'), 400);
        return;
      }
      showToast('success', 'Araç başarıyla güncellendi.');
      setTimeout(() => navigate('/shipment-vehicles'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut form bilgilerini taşıyarak yeni-kayıt ekranını aç. Kullanıcı Kaydet diyene kadar hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/shipment-vehicles/new', { state: { clone: { ...form }, cloneCriteria: JSON.stringify(criteria) } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Araç başarıyla silindi.');
      setTimeout(() => navigate('/shipment-vehicles'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusMeta = STATUS_META[form.status];
  const statusBadge = !isNew && statusMeta
    ? <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>
    : null;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/shipment-vehicles')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni araç aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Sevkiyat Araçları', to: '/shipment-vehicles' }, { label: isNew ? 'Yeni Sevkiyat Aracı' : 'Sevkiyat Aracı Detayı', current: true }]}
      status={statusBadge}
      tabs={TABS}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'ShipmentVehicle', entityId: id }}
      loading={loading}
    >
      <div className="dps-form" style={{ display: activeTab === 'vehicle' ? undefined : 'none' }}>
        <Section icon="box" title="Araç Bilgileri" subtitle="Plaka, marka, model ve kapasite bilgileri">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="plaka" label="Plaka" req mono />
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} />
            <FieldText form={form} set={set} k="brand" label="Marka" />
            <FieldText form={form} set={set} k="model" label="Model" />
            <FieldText form={form} set={set} k="volume" label="Hacim" num />
            <FieldText form={form} set={set} k="maxWeight" label="Max Ağırlık" num />
          </Grid>
        </Section>

        <Section icon="layers" title="İlişkiler" subtitle="Distribütör ve depo ataması">
          <Grid cols={2}>
            <FieldCustom label="Distribütör" req invalid={tried && !form.vendorId}>
              <RelationPicker
                config={RELATION_CONFIGS.vendor}
                rows={vendors}
                value={form.vendorId}
                selectedLabel={form.vendorId ? getVendorLabel(form.vendorId) : ''}
                onChange={(val) => set('vendorId', val)}
                placeholder="Seçiniz"
              />
            </FieldCustom>
            <FieldCustom label="Depo">
              <RelationPicker
                config={RELATION_CONFIGS.warehouse}
                rows={warehouses}
                value={form.warehouseId}
                selectedLabel={form.warehouseId ? getWarehouseLabel(form.warehouseId) : ''}
                onChange={(val) => set('warehouseId', val)}
                placeholder="Seçiniz"
              />
            </FieldCustom>
            <FieldCustom label="Dağıtıcı">
              <RelationPicker
                config={RELATION_CONFIGS.distributor}
                rows={distributors}
                value={form.distributorId}
                selectedLabel={form.distributorId ? getDistributorLabel(form.distributorId) : ''}
                onChange={(val) => set('distributorId', val)}
                placeholder="Seçiniz"
              />
            </FieldCustom>
          </Grid>
        </Section>
      </div>

      {activeTab === 'criteria' && (
        <ShipmentCriteriaTab criteria={criteria} setCriteria={setCriteria} />
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.plaka}”</strong> aracını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default ShipmentVehicleDetailPage;
